import React, { useState, useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reducer, initialState } from '../../../reducers/refineAuthorization/containerReducer';
import Fragments from '../../../fragments';
import { SAVE_AUTHORIZATION_X_DOCUMENT_TYPE_X_USER_GROUPS } from '../../../mutations';
import { AUTHORIZATION_X_DOCUMENT_TYPE_X_USER_GROUPS_INVALIDATED } from '../../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useListBrowserUtils } from '@ts-private/list-browser';
import { refineAuthorizationStore } from './RefineAuthorizationListBrowser';
import RefineAuthorizationListTable from './RefineAuthorizationListTable';
import { AUTHORIZATION_WITH_REFINES_QUERY } from '../../../queries';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { Box } from '@mui/material';

const RefineAuthorizationContainer = ({ onSaveFnChanged, onChangedTitle, stateRef, authorizationId }) => {
  const { store } = useListBrowserUtils(refineAuthorizationStore);
  const client = useApolloClient();
  const addToast = useToast();
  const { t } = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [rendered, setRendered] = useState(false);

  const { loading: authorizationLoading, data: authorization } = useQuery(AUTHORIZATION_WITH_REFINES_QUERY, {
    variables: {
      authorizationId: authorizationId
    },
    skip: !authorizationId
  });

  useQuery(USER_GROUPS_QUERY, {
    onCompleted: data => {
      stateRef.current = {
        ...stateRef.current,
        data: data.userGroupsConfig
      };
      const { handleUserGroupsChange } = stateRef.current;
      handleUserGroupsChange && handleUserGroupsChange(data.userGroupsConfig);
    }
  });

  const [saveAuthorizationXDocumentTypeXUserGroups] = useMutation(SAVE_AUTHORIZATION_X_DOCUMENT_TYPE_X_USER_GROUPS);

  useSubscription(AUTHORIZATION_X_DOCUMENT_TYPE_X_USER_GROUPS_INVALIDATED, {
    onData: authorizationXDocumentTypeXUserGroupsInvalidatedData => {
      const { authorizationXDocumentTypeXUserGroups } =
        authorizationXDocumentTypeXUserGroupsInvalidatedData.data.data.authorizationXDocumentTypeXUserGroupsInvalidated;
      authorizationXDocumentTypeXUserGroups.forEach(axdtxug => {
        if (axdtxug.canRead) {
          const newAuthorizationXDocumentTypeXUserGroup = {
            __typename: 'AuthorizationXDocumentTypeXUserGroup',
            authorizationId: axdtxug.authorizationId,
            userGroupId: axdtxug.userGroupId,
            documentTypeId: axdtxug.documentTypeId,
            documentStateId: axdtxug.documentStateId,
            canRead: axdtxug.canRead
          };
          client.cache.modify({
            id: `AuthorizationWithRefines:{"webAuthorizationId":${axdtxug.authorizationId}}`,
            fields: {
              authorizationXDocumentTypeXUserGroups(existingAuthorizationXDocumentTypeXUserGroups = [], { readField }) {
                const newRef = client.cache.writeFragment({
                  data: newAuthorizationXDocumentTypeXUserGroup,
                  fragment: Fragments.authorizationXDocumentTypeXUserGroup
                });

                if (
                  existingAuthorizationXDocumentTypeXUserGroups.some(
                    ref =>
                      newAuthorizationXDocumentTypeXUserGroup.authorizationId === readField('authorizationId', ref) &&
                      newAuthorizationXDocumentTypeXUserGroup.documentTypeId === readField('documentTypeId', ref) &&
                      newAuthorizationXDocumentTypeXUserGroup.documentStateId === readField('documentStateId', ref) &&
                      newAuthorizationXDocumentTypeXUserGroup.userGroupId === readField('userGroupId', ref)
                  )
                ) {
                  return existingAuthorizationXDocumentTypeXUserGroups;
                }

                return [...existingAuthorizationXDocumentTypeXUserGroups, newRef];
              }
            }
          });
          client.cache.modify({
            id: `Authorization:{"webAuthorizationId":${axdtxug.authorizationId}}`,
            fields: {
              isRefined() {
                return true;
              },
              authorizationXUserGroups() {
                return [];
              }
            }
          });
        } else {
          let isRefined = true;
          client.cache.modify({
            id: `AuthorizationWithRefines:{"webAuthorizationId":${axdtxug.authorizationId}}`,
            fields: {
              authorizationXDocumentTypeXUserGroups(existingAuthorizationXDocumentTypeXUserGroups, { readField }) {
                const filteredAuthorizationXDocumentTypeXUserGroups = existingAuthorizationXDocumentTypeXUserGroups.filter(
                  ref =>
                    axdtxug.documentTypeId !== readField('documentTypeId', ref) ||
                    axdtxug.documentStateId !== readField('documentStateId', ref) ||
                    axdtxug.userGroupId !== readField('userGroupId', ref)
                );
                if (filteredAuthorizationXDocumentTypeXUserGroups.length === 0) isRefined = false;
                return filteredAuthorizationXDocumentTypeXUserGroups;
              }
            }
          });
          client.cache.modify({
            id: `Authorization:{"webAuthorizationId":${axdtxug.authorizationId}}`,
            fields: {
              isRefined() {
                return isRefined;
              }
            }
          });
        }
      });
      client.cache.gc();
      dispatch({ type: 'OnResetChangedRefinePermission' });
      addToast(t('Authorization.AuthorizationXDocumentTypeXUserGroupsSaved'), 'success');
    }
  });

  const onSave = useCallback(() => {
    const convertToObject = authXDTXUG => {
      let authorizationId = localState.authorization?.webAuthorizationId;
      let object = [];
      for (let ds in authXDTXUG) {
        const { documentTypeId, ...documentStates } = authXDTXUG[ds];

        for (let ug in documentStates) {
          const { documentStateId, ...others } = documentStates[ug];
          for (var user in others) {
            object.push({
              authorizationId: authorizationId,
              documentTypeId: documentTypeId,
              documentStateId: documentStateId,
              userGroupId: parseInt(user),
              canRead: others[user]
            });
          }
        }
      }

      return object;
    };
    if (!localState.disabledSave) {
      saveAuthorizationXDocumentTypeXUserGroups({
        variables: {
          input: convertToObject(localState.changedRefinePermissions)
        }
      });
      dispatch({ type: 'OnSaveAuthorizationXDocumentTypeXUserGroups' });
    }
  }, [
    localState.authorization?.webAuthorizationId,
    localState.changedRefinePermissions,
    localState.disabledSave,
    saveAuthorizationXDocumentTypeXUserGroups
  ]);

  const handleUserGroupsChange = userGroups => dispatch({ type: 'OnInitUserGroups', userGroups });

  useEffect(() => {
    if (stateRef && stateRef.current) {
      stateRef.current = {
        ...stateRef.current,
        handleUserGroupsChange
      };
      dispatch({
        type: 'OnInitUserGroups',
        userGroups:
          stateRef.current.data?.filter(x => x.name.toLowerCase().includes(store.filters.userGroups?.toLowerCase() || '') ?? true) || []
      });
      dispatch({
        type: 'OnInitChangedRefinePermissions',
        changedRefinePermissions: stateRef.current.changedRefinePermissions || {}
      });
      dispatch({
        type: 'OnInitDisabledSave',
        disabledSave: stateRef.current.disabledSave || stateRef.current.disabledSave === false ? stateRef.current.disabledSave : true
      });
    }
  }, [stateRef, store.filters.userGroups]);

  useEffect(() => {
    stateRef.current = {
      ...stateRef.current,
      changedRefinePermissions: localState.changedRefinePermissions
    };
  }, [localState.changedRefinePermissions, stateRef]);

  useEffect(() => {
    stateRef.current = {
      ...stateRef.current,
      disabledSave: localState.disabledSave
    };
  }, [localState.disabledSave, stateRef]);

  useEffect(() => {
    if (authorization && rendered) onChangedTitle(authorization.authorizationWithRefines.name);
  }, [authorization, onChangedTitle, rendered]);

  useEffect(() => {
    if (!rendered) return;
    onSaveFnChanged(onSave, localState.disabledSave);
  }, [onSave, localState.disabledSave, rendered, onSaveFnChanged]);

  useEffect(() => {
    setRendered(true);
  }, [rendered]);

  useEffect(() => {
    if (authorization) dispatch({ type: 'OnInitAuthorization', authorization: authorization.authorizationWithRefines });
  }, [authorization]);

  useEffect(() => {
    if (store.values)
      dispatch({
        type: 'OnInitDocumentTypeWithAllowedStates',
        documentTypeWithAllowedStates: store.values
      });
  }, [store.values]);

  return (
    <Box mt={2}>
      <Prompt when={!localState.disabledSave} message={t('Authorization.LeavingWithoutSavingAuthorizationRefines')} />
      <RefineAuthorizationListTable
        dispatch={dispatch}
        loading={authorizationLoading || store.loading}
        userGroups={localState.userGroups}
        allowedStates={localState.documentTypeWithAllowedStates}
        changedRefinePermissions={localState.changedRefinePermissions}
        authorization={localState.authorization}
        onSave={onSave}
      />
    </Box>
  );
};

RefineAuthorizationContainer.propTypes = {
  onSaveFnChanged: PropTypes.func.isRequired,
  onChangedTitle: PropTypes.func.isRequired,
  authorizationId: PropTypes.number.isRequired,
  stateRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

export default RefineAuthorizationContainer;
